import { useDispatch, useSelector } from 'react-redux';

import {
  setAllContentSuggestions,
  selectAllContentSuggestions,
} from 'components/modules/content/slices';
import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import useRequest from 'components/modules/foundation/hooks/use-request';
import API from 'libs/api';
import { selectWebsite } from 'selectors/website';

export default function useContentSuggestions({ fetchOnMount = true, reviewed = false } = {}) {
  const dispatch = useDispatch();

  const website = useSelector(selectWebsite);

  const suggestions = useSelector(selectAllContentSuggestions);

  const { loading, request, withRequest } = useRequest();

  const { id: websiteId } = website || {};

  function createQueryParams() {
    return {
      website_id: websiteId,
      reviewed,
    };
  }

  async function fetchContentSuggestions() {
    const { data } = await API.getContentSuggestions(createQueryParams());

    dispatch(setAllContentSuggestions(data));
  }

  async function reviewContentSuggestions(reviewedSuggestions) {
    const suggestionsToReview = Object.entries(reviewedSuggestions)
      .filter(([, { approved, rejected }]) => approved || rejected)
      .map(([id, suggestion]) => ({
        id,
        ...suggestion,
      }));

    return Promise.all(
      suggestionsToReview.map(({ id, approved, reason, acceptedValue }) =>
        API.reviewContentSuggestions(id, {
          approved,
          reason,
          acceptedValue,
        }),
      ),
    );
  }

  useAsyncEffect(async () => {
    if (fetchOnMount) {
      await request(fetchContentSuggestions);
    }
  }, [websiteId]);

  return {
    suggestions,
    loading,
    reviewContentSuggestions: withRequest(reviewContentSuggestions),
    refreshContentSuggestions: withRequest(fetchContentSuggestions),
  };
}
